<script setup lang="ts">
import {
  ANNOTATOR_TYPES,
  AnnotatorType,
  SerializedBarnDocChartAnnotationProvider,
  SerializedChartAnnotatorType,
  SerializedSparseDictChartAnnotationProvider,
  SerializedTimespanChartIncidentAnnotator,
} from '@/composables/charts/influx-charts/ChartIncidentAnnotators'
import { computed, ref, Ref } from 'vue'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import AddFilterForm from '@/components/AddFilterForm.vue'
import AppliedFiltersList from '@/components/AppliedFiltersList.vue'
import { useFilter } from '@/composables/filters/filters'
import { useI18n } from 'vue-i18n'
import { useCameraName } from '@/composables/cameraHelpers'
import { humanReadableTimestamp } from '@/composables/datetime'
import { ALL, ANY, CONTAINS, IN, NIN } from '@/composables/filters/operators'
import { useDisplay } from 'vuetify'
import { DocumentQuerySelect } from '@/types/Query'

const annotators = defineModel<SerializedChartAnnotatorType[]>({ required: true })

const { t } = useI18n()
const { mobile } = useDisplay()

const defaultTimespan: SerializedTimespanChartIncidentAnnotator = {
  annotatorType: ANNOTATOR_TYPES.TIMESPAN,
  incidentTypes: [],
  title: '',
  additionalFilters: [],
  shapeFillColor: '',
}

const defaultBarnDoc: SerializedBarnDocChartAnnotationProvider = {
  annotatorType: ANNOTATOR_TYPES.BARNDOC,
  title: '',
  additionalFilters: [],
}

const defaultSparseDict: SerializedSparseDictChartAnnotationProvider = {
  annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY,
  title: '',
  additionalFilters: [],
  detectionIdentifier: '',
}

const annotatorTypes = [
  { title: t('charts.annotators.timespan'), value: ANNOTATOR_TYPES.TIMESPAN },
  { title: t('charts.annotators.barndoc'), value: ANNOTATOR_TYPES.BARNDOC },
  { title: t('charts.annotators.timeseries.sparse_dict_anomaly_detection'), value: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY },
]

const dialogOpen = ref(false)
const editingIndex = ref(-1)
const addFilterDialog = ref(false)
const currentAnnotator: Ref<SerializedChartAnnotatorType> = ref({ ...defaultTimespan })

const incidentTypes = computed(() => {
  const filter = useFilter('incident')
  return filter.getOptions('type').map((opt: { id: string; title: string }) => ({
    title: opt.title,
    value: opt.id,
  }))
})

const appliedFiltersDisplay = computed(() => {
  if (!currentAnnotator.value.additionalFilters) return []
  return currentAnnotator.value.additionalFilters.map(filter => {
    return {
      text: hydrateFilterText(filter),
      forced: false,
    }
  })
})

const openAddDialog = () => {
  editingIndex.value = -1
  currentAnnotator.value = { ...defaultTimespan }
  dialogOpen.value = true
}

const openEditDialog = (index: number) => {
  editingIndex.value = index
  currentAnnotator.value = JSON.parse(JSON.stringify(annotators.value[index]))
  dialogOpen.value = true
}

const deleteAnnotator = (index: number) => {
  annotators.value.splice(index, 1)
}

const saveAnnotator = () => {
  if (editingIndex.value === -1) {
    annotators.value.push(currentAnnotator.value)
  } else {
    annotators.value[editingIndex.value] = currentAnnotator.value
  }
  dialogOpen.value = false
}

const onTypeChange = (newType: AnnotatorType) => {
  const base = {
    title: currentAnnotator.value.title,
    additionalFilters: currentAnnotator.value.additionalFilters,
  }
  if (newType === ANNOTATOR_TYPES.TIMESPAN) {
    currentAnnotator.value = { ...defaultTimespan, ...base }
  } else if (newType === ANNOTATOR_TYPES.BARNDOC) {
    currentAnnotator.value = { ...defaultBarnDoc, ...base }
  } else if (newType === ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY) {
    currentAnnotator.value = { ...defaultSparseDict, ...base }
  }
}

const addFilter = (filter: DocumentQuerySelect) => {
  if (!currentAnnotator.value.additionalFilters) {
    currentAnnotator.value.additionalFilters = []
  }
  currentAnnotator.value.additionalFilters.push(filter)
  addFilterDialog.value = false
}

const removeFilter = (index: number) => {
  currentAnnotator.value.additionalFilters?.splice(index, 1)
}

function hydrateFilterText(filter: DocumentQuerySelect): string {
  const filterConfig = useFilter('incident')
  const propertyTitle = filterConfig.getPropertyTitle(filter.key)
  const dataType = filterConfig.getDataType(filter.key)
  const isArrayOperator = [NIN, IN, CONTAINS, ALL, ANY].includes(filter.operator)
  let valueText = ''

  if (dataType === 'camera') {
    if (!isArrayOperator) {
      valueText = useCameraName(filter.value as string)
    } else {
      const cameraNames = []
      for (const cameraId of filter.value as string[]) {
        cameraNames.push(useCameraName(cameraId))
      }
      valueText = cameraNames.join(', ')
    }
  } else if (dataType === 'String' || dataType === 'Number' || dataType === 'generic') {
    valueText = String(filter.value)
  } else if (dataType === 'timestamp') {
    valueText = humanReadableTimestamp(filter.value as number)
  } else if (dataType === 'autocomplete') {
    if (!isArrayOperator) {
      valueText = filterConfig.getOptionTitle(filter.key, filter.value)
    } else {
      const optionTitles = []
      for (const optionId of filter.value as string[]) {
        optionTitles.push(filterConfig.getOptionTitle(filter.key, optionId))
      }
      valueText = optionTitles.join(', ')
    }
  }

  return `${propertyTitle} ${filter.operator} ${valueText}`
}
</script>

<template>
  <v-layout-card
    class="pa-0"
    variant="flat"
    elevation="0"
  >
    <v-card-title class="pt-0">
      <v-icon
        icon="mdi-bookmark-plus"
        start
      />
      {{ $t('chart_sharing.creator.customize.annotator.title') }}
    </v-card-title>
    <v-card-text
      class="py-0"
      :class="{ 'px-0': mobile, 'px-6': !mobile }"
    >
      <div
        v-if="annotators.length === 0"
        class="mt-2 mb-8 d-flex justify-center opacity-70"
      >
        <v-icon
          icon="mdi-bookmark-off-outline"
          start
        />
        <span class="font-italic">
          {{ $t('chart_sharing.creator.customize.annotator.nothing_added_yet') }}
        </span>
      </div>

      <v-list v-if="annotators.length > 0">
        <v-list-item
          v-for="(annotator, index) in annotators"
          :key="index"
          class="px-0"
          :class="{ 'mb-2': index !== annotators.length - 1 }"
          :title="annotator.title || annotator.annotatorType"
          :subtitle="annotator.annotatorType"
        >
          <template #prepend>
            <v-avatar variant="tonal">
              <span class="text-body-1 font-weight-medium">
                {{ index + 1 }}
              </span>
            </v-avatar>
          </template>
          <template #append>
            <div class="d-flex align-center ga-4">
              <v-btn
                class="rounded-circle"
                color="primary"
                icon
                variant="tonal"
                size="small"
                @click="openEditDialog(index)"
              >
                <v-icon
                  icon="mdi-pencil"
                  size="large"
                />
              </v-btn>
              <v-btn
                class="rounded-circle"
                icon
                variant="tonal"
                size="small"
                color="error"
                @click="deleteAnnotator(index)"
              >
                <v-icon
                  icon="mdi-delete-forever"
                  size="large"
                />
              </v-btn>
            </div>
          </template>
        </v-list-item>
      </v-list>

      <div class="d-flex justify-center">
        <v-btn
          prepend-icon="mdi-plus"
          variant="tonal"
          color="primary"
          @click="openAddDialog"
        >
          {{ $t('chart_sharing.creator.customize.annotator.add_annotator') }}
        </v-btn>
      </div>
    </v-card-text>
  </v-layout-card>

  <GenericCustomDialog
    v-model="dialogOpen"
    max-width="600px"
    action-class="justify-space-between"
  >
    <template #headerIcon>
      <v-icon :icon="editingIndex === -1 ? 'mdi-plus' : 'mdi-pencil'" />
    </template>
    <template #title>
      {{
        editingIndex === -1
          ? $t('chart_sharing.creator.customize.annotator.add_annotator')
          : $t('chart_sharing.creator.customize.annotator.edit_annotator')
      }}
    </template>
    <template #body>
      <v-container>
        <v-select
          :model-value="currentAnnotator.annotatorType"
          prepend-icon="mdi-bookmark"
          :items="annotatorTypes"
          :label="$t('chart_sharing.creator.customize.annotator.annotator_type')"
          variant="outlined"
          @update:model-value="onTypeChange"
        />

        <v-text-field
          v-model="currentAnnotator.title"
          prepend-icon="mdi-format-header-1"
          :label="$t('chart_sharing.creator.customize.chart_title')"
          variant="outlined"
        />

        <!-- Timespan Specifics -->
        <template v-if="currentAnnotator.annotatorType === ANNOTATOR_TYPES.TIMESPAN">
          <v-combobox
            v-model="(currentAnnotator as SerializedTimespanChartIncidentAnnotator).incidentTypes"
            prepend-icon="mdi-alert"
            :items="incidentTypes"
            :label="$t('chart_sharing.creator.customize.annotator.incident_types')"
            multiple
            chips
            variant="outlined"
            item-title="title"
            item-value="value"
            :return-object="false"
          />

          <v-text-field
            v-model="(currentAnnotator as SerializedTimespanChartIncidentAnnotator).shapeFillColor"
            prepend-icon="mdi-format-color-fill"
            :label="$t('chart_sharing.creator.customize.annotator.shape_fill_color')"
            variant="outlined"
          >
            <template #append>
              <v-menu
                :close-on-content-click="false"
                location="end"
              >
                <template #activator="{ props }">
                  <div
                    v-bind="props"
                    :style="{
                      backgroundColor: (currentAnnotator as SerializedTimespanChartIncidentAnnotator).shapeFillColor || 'transparent',
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                    }"
                  />
                </template>
                <v-color-picker
                  v-model="(currentAnnotator as SerializedTimespanChartIncidentAnnotator).shapeFillColor"
                  mode="rgba"
                  hide-inputs
                />
              </v-menu>
            </template>
          </v-text-field>
        </template>

        <!-- Sparse Dict Specifics -->
        <template v-if="currentAnnotator.annotatorType === ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY">
          <v-text-field
            v-model="(currentAnnotator as SerializedSparseDictChartAnnotationProvider).detectionIdentifier"
            prepend-icon="mdi-identifier"
            :label="$t('chart_sharing.creator.customize.annotator.detection_identifier')"
            variant="outlined"
          />
        </template>

        <v-divider class="my-4" />

        <div class="d-flex align-center justify-space-between mb-2">
          <div class="text-subtitle-1">{{ $t('chart_sharing.creator.customize.annotator.additional_filters') }}</div>
          <v-btn
            size="small"
            prepend-icon="mdi-plus"
            variant="text"
          >
            {{ $t('chart_sharing.creator.customize.annotator.add_filter') }}
            <AddFilterForm
              v-model="addFilterDialog"
              filter-type="incident"
              @confirm="addFilter"
            />
          </v-btn>
        </div>

        <AppliedFiltersList
          :applied-filters="appliedFiltersDisplay"
          :remove-filter="removeFilter"
        />
      </v-container>
    </template>
    <template #actions>
      <v-btn
        append-icon="mdi-check"
        color="success"
        variant="flat"
        @click="saveAnnotator"
      >
        {{ $t('general_interface.buttons.save') }}
      </v-btn>
    </template>
  </GenericCustomDialog>
</template>
